import {
  buildPath,
  buildTemplatePath,
  ListContainer,
  ListContext,
  Page,
  
  PageRoute,
  pn,
  SearchBar,
  Stack,
} from "@compass/core";
import { ListFooter } from "components";
import {
  SurveyResponseFilter as SurveyResponseFilterModel,
  SurveyResponseListDto,
  SurveyResponseListFilter,
  SurveyResponseListResource,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";
import { SurveyResponseFilter } from "../SurveyResponseFilter";
import { SurveyResponseTable } from "./SurveyResponseTable";

const routeName = "Responses";

export const surveyResponseListPageRoute: PageRoute = {
  handle: "Responses",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const SurveyResponseListPage = () => {
  const { listFilter, setListFilter } = useInitListFilter<SurveyResponseListFilter>({
    sortObjects: [{ property: pn<SurveyResponseListDto>("completedDate"), isDesc: true }],
  });
  const [initListFilter] = useState<SurveyResponseListFilter>(listFilter);
  const [listItems, setListItems] = useState<SurveyResponseListDto[]>([]);
  const [resource, setResource] = useState<SurveyResponseListResource>({} as SurveyResponseListResource);
  const [total, setTotal] = useState<number>();
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, loadList, applyPage, applyPageSize, applySortMethod, applySort, applySearch } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.surveyResponses.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.surveyResponses.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.surveyResponses.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<SurveyResponseListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <SurveyResponseFilter
        filter={listFilter.surveyResponseFilter || ({} as SurveyResponseFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.surveyResponseFilter = filter;
            })
          )
        }
      />
    ),
  });

  return (
    <Page header={surveyResponseListPageRoute.handle}>
      <ListContainer
        fullHeight
        header={
          <Stack direction="row" justifyContent="space-between">
            <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
            {listFilterNode}
          </Stack>
        }
        tableContainer={
          <ListContext.Provider value={{ loadList: () => loadList(listFilter) }}>
            <SurveyResponseTable
              tableContainerRef={tableContainerRef}
              surveyResponses={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
            />
          </ListContext.Provider>
        }
        footer={
          <ListFooter
            total={total}
            size={listFilter.pageSize}
            page={listFilter.page}
            onPageChange={applyPage}
            onSizeChange={applyPageSize}
            onSortMethodChange={applySortMethod}
          />
        }
      />
    </Page>
  );
};
